import React from 'react';
import { Link } from 'react-router-dom';
import { authManager } from '../../services/authManager';
import { ESPACE_ADMIN } from '../../utils/espace';

export type OngletMemoire =
  | 'apercu' | 'documents' | 'fiches' | 'faits' | 'conflits' | 'sources' | 'competences' | 'modeles'
  | 'comptes' | 'parametres';

const MEMOIRE: Array<{ cle: OngletMemoire; libelle: string }> = [
  { cle: 'apercu', libelle: "Vue d'ensemble" },
  { cle: 'documents', libelle: 'Documents' },
  { cle: 'fiches', libelle: 'Fiches' },
  { cle: 'faits', libelle: 'Faits' },
  { cle: 'conflits', libelle: 'À trancher' },
  { cle: 'sources', libelle: 'Sources' },
  { cle: 'competences', libelle: 'Compétences' },
  { cle: 'modeles', libelle: 'Modèles' },
];

const ADMINISTRATION: Array<{ cle: OngletMemoire; libelle: string; href: string }> = [
  { cle: 'comptes', libelle: 'Comptes et usage', href: '/workspace/memoire/comptes' },
  { cle: 'parametres', libelle: 'Paramètres', href: '/workspace/memoire/parametres' },
];

interface Props {
  actif: OngletMemoire;
  /** Nombre de désaccords ouverts, affiché sur l'onglet « À trancher ». */
  conflits?: number;
}

/**
 * En-tête de l'espace Mémoire du PEV.
 *
 * L'administration y est rangée : administrer la plateforme revient surtout à
 * décider de ce qu'elle sait — les documents qui font autorité, la hiérarchie
 * des sources — puis de qui y accède.
 */
const NavMemoire: React.FC<Props> = ({ actif, conflits = 0 }) => {
  const estAdmin = authManager.hasRole('admin');

  const onglet = (cle: OngletMemoire, libelle: string, href: string, pastille?: number) => (
    <Link
      key={cle}
      to={href}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-medium
        transition-colors ${
          actif === cle
            ? 'bg-eg-ombre text-white'
            : 'text-eg-gris hover:bg-black/[0.045] hover:text-eg-encre'
        }`}
    >
      {libelle}
      {!!pastille && (
        <span className={`rounded-full px-1.5 text-[10.5px] ${
          actif === cle ? 'bg-white/20' : 'bg-eg-alerte/15 text-eg-alerte'
        }`}
        >
          {pastille}
        </span>
      )}
    </Link>
  );

  // Sur le site d'administration, le menu latéral porte la navigation : on ne
  // garde ici que le titre, et rien sur les pages qui ont déjà le leur.
  if (ESPACE_ADMIN && (actif === 'comptes' || actif === 'parametres')) return null;

  return (
    <div className="mb-7">
      <h1 className="text-[28px] font-semibold tracking-tight text-eg-encre">Mémoire du PEV</h1>
      <p className="mt-1.5 max-w-[68ch] text-[14.5px] text-eg-gris">
        Ce que la plateforme sait du programme, et de quelle source elle le tient. Tous les
        assistants la lisent ; les administrateurs l'alimentent.
      </p>
      <div className={`mt-5 flex items-center gap-1 overflow-x-auto pb-1 ${ESPACE_ADMIN ? 'hidden' : ''}`}>
        {MEMOIRE.map((o) =>
          onglet(o.cle, o.libelle, o.cle === 'apercu' ? '/workspace/memoire' : `/workspace/memoire/${o.cle}`,
            o.cle === 'conflits' ? conflits : undefined))}
        {estAdmin && (
          <>
            <span className="mx-2 h-5 w-px shrink-0 bg-eg-bord" />
            <span className="shrink-0 px-1 text-[11.5px] font-medium text-eg-estompe">Administration</span>
            {ADMINISTRATION.map((o) => onglet(o.cle, o.libelle, o.href))}
          </>
        )}
      </div>
    </div>
  );
};

export default NavMemoire;
